import { z } from 'zod';

import { apiKeyScopeSchema, userRoleSchema } from './enums.js';

export const loginSchema = z
  .object({
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(1),
  })
  .strict();

export const loginResponseSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number().int(),
  user: z.object({
    id: z.string(),
    email: z.string().email(),
    role: userRoleSchema,
  }),
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const currentUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  role: userRoleSchema,
});
export type CurrentUser = z.infer<typeof currentUserSchema>;

/** Minimum password policy for dashboard users. */
export const passwordSchema = z
  .string()
  .min(12, 'Use at least 12 characters')
  .max(200)
  .refine((v) => /[a-z]/.test(v) && /[A-Z]/.test(v) && /\d/.test(v), {
    message: 'Use upper case, lower case and a digit',
  });

export const createUserSchema = z
  .object({
    email: z.string().trim().toLowerCase().email(),
    password: passwordSchema,
    role: userRoleSchema,
  })
  .strict();

export const updateUserSchema = z
  .object({
    role: userRoleSchema.optional(),
    active: z.boolean().optional(),
    password: passwordSchema.optional(),
  })
  .strict();

export const userDtoSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  role: userRoleSchema,
  active: z.boolean(),
  createdAt: z.string().datetime(),
});
export type UserDto = z.infer<typeof userDtoSchema>;

export const createApiKeySchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    scopes: z.array(apiKeyScopeSchema).min(1),
  })
  .strict();

export const apiKeyDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  scopes: z.array(z.string()),
  active: z.boolean(),
  lastUsedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type ApiKeyDto = z.infer<typeof apiKeyDtoSchema>;

/** The raw key is returned exactly once, at creation. */
export const createApiKeyResponseSchema = apiKeyDtoSchema.extend({ key: z.string() });
